import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// ─── Company details ───────────────────────────────
const COMPANY = {
  name: 'SwissChem',
  tagline: 'Textile & dye chemicals',
  address: '82-C Mehar Sadiq Market, Railway Road, Faisalabad',
  phone: '+92 300 7671404',
  landline: '041-2611464',
  email: 'ceo@swisschem.pk',
  website: 'www.swisschem.pk',
  strn: '3310443600991',
  ntn: '5853163-4',
};

const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
};

const titleCase = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '—');

/**
 * Generate and download a Gate Pass PDF.
 * @param {object} gp - full gate pass object (with items[]) from getGatePassById
 */
export const generateGatePassPdf = (gp) => {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const marginX = 14;
  let y = 16;

  // ─── Company header ───────────────────────────────
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.setTextColor(231, 76, 60); // brand red
  doc.text(COMPANY.name, marginX, y);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(120, 120, 120);
  doc.text(COMPANY.tagline, marginX, y + 6);

  // Right-aligned contact block
  doc.setFontSize(8.5);
  doc.setTextColor(90, 90, 90);
  const rightX = pageWidth - marginX;
  doc.text(COMPANY.address, rightX, y, { align: 'right' });
  doc.text(`Ph: ${COMPANY.phone}  |  ${COMPANY.landline}`, rightX, y + 4.5, { align: 'right' });
  doc.text(`STRN: ${COMPANY.strn}`, rightX, y + 9, { align: 'right' });
  doc.text(`NTN: ${COMPANY.ntn}`, rightX, y + 13.5, { align: 'right' });

  y += 20;

  // ─── Divider ──────────────────────────────────────
  doc.setDrawColor(220, 220, 220);
  doc.setLineWidth(0.4);
  doc.line(marginX, y, pageWidth - marginX, y);
  y += 10;

  // ─── Title: GATE PASS ─────────────────────────────
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(44, 62, 80);
  doc.text('GATE PASS', pageWidth / 2, y, { align: 'center' });

  // Direction badge text under title
  doc.setFontSize(10);
  doc.setTextColor(231, 76, 60);
  const dirLabel = gp.direction === 'outward' ? 'OUTWARD (Goods Out)' : 'INWARD (Goods In)';
  doc.text(dirLabel, pageWidth / 2, y + 6, { align: 'center' });
  y += 14;

  // ─── Meta info box (GP no, date, status) ──────────
  doc.setFontSize(10);
  doc.setTextColor(44, 62, 80);

  const leftColX = marginX;
  const rightColX = pageWidth / 2 + 5;

  doc.setFont('helvetica', 'bold');
  doc.text('GP No:', leftColX, y);
  doc.setFont('helvetica', 'normal');
  doc.text(String(gp.gp_no || '—'), leftColX + 20, y);

  doc.setFont('helvetica', 'bold');
  doc.text('Date:', rightColX, y);
  doc.setFont('helvetica', 'normal');
  doc.text(formatDate(gp.gp_date), rightColX + 20, y);
  y += 6;

  doc.setFont('helvetica', 'bold');
  doc.text('Status:', leftColX, y);
  doc.setFont('helvetica', 'normal');
  doc.text(titleCase(gp.status), leftColX + 20, y);

  doc.setFont('helvetica', 'bold');
  doc.text(gp.party_type === 'customer' ? 'Customer:' : 'Vendor:', rightColX, y);
  doc.setFont('helvetica', 'normal');
  doc.text(gp.party_name || '—', rightColX + 20, y);
  y += 6;

  if (gp.party_phone) {
    doc.setFont('helvetica', 'bold');
    doc.text('Party Ph:', rightColX, y);
    doc.setFont('helvetica', 'normal');
    doc.text(String(gp.party_phone), rightColX + 20, y);
    y += 6;
  }

  y += 4;

  // ─── Rider / Vehicle box ──────────────────────────
  doc.setDrawColor(230, 230, 230);
  doc.setFillColor(248, 249, 250);
  doc.roundedRect(marginX, y, pageWidth - marginX * 2, 22, 2, 2, 'FD');

  doc.setFontSize(9);
  doc.setTextColor(127, 140, 141);
  doc.setFont('helvetica', 'bold');
  doc.text('RIDER / TRANSPORT DETAILS', marginX + 4, y + 6);

  doc.setTextColor(44, 62, 80);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.5);
  doc.text(`Rider: ${gp.rider_name || '—'}`, marginX + 4, y + 12);
  doc.text(`CNIC: ${gp.rider_cnic || '—'}`, marginX + 70, y + 12);
  doc.text(`Phone: ${gp.rider_phone || '—'}`, marginX + 4, y + 18);
  doc.text(`Vehicle: ${gp.vehicle_no || '—'}`, marginX + 70, y + 18);

  y += 30;

  // ─── Items table ──────────────────────────────────
  const rows = (gp.items || []).map((it, idx) => [
    idx + 1,
    `${it.product_name || '—'}${it.product_code ? `\n(${it.product_code})` : ''}`,
    it.batch_no || '—',
    `${parseFloat(it.quantity || 0).toFixed(2).replace(/\.?0+$/, '')} ${it.unit || ''}`,
    it.packaging_qty != null ? String(it.packaging_qty) : '—',
    it.remarks || '—',
  ]);

  autoTable(doc, {
    startY: y,
    head: [['#', 'Product', 'Batch No', 'Quantity', 'Packages', 'Remarks']],
    body: rows.length > 0 ? rows : [['—', 'No items', '—', '—', '—', '—']],
    theme: 'grid',
    headStyles: {
      fillColor: [44, 62, 80],
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 9,
    },
    bodyStyles: { fontSize: 9, textColor: [44, 62, 80] },
    columnStyles: {
      0: { cellWidth: 10, halign: 'center' },
      2: { cellWidth: 28 },
      3: { cellWidth: 26, halign: 'right' },
      4: { cellWidth: 22, halign: 'center' },
    },
    margin: { left: marginX, right: marginX },
  });

  let afterTableY = doc.lastAutoTable.finalY + 10;

  // ─── Remarks (gate-pass level) ────────────────────
  if (gp.remarks) {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(127, 140, 141);
    doc.text('Remarks:', marginX, afterTableY);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(44, 62, 80);
    const wrapped = doc.splitTextToSize(gp.remarks, pageWidth - marginX * 2 - 22);
    doc.text(wrapped, marginX + 20, afterTableY);
    afterTableY += wrapped.length * 5 + 6;
  }

  // ─── Signature lines ──────────────────────────────
  // Push signatures near the bottom if there's room, else just below content
  const pageHeight = doc.internal.pageSize.getHeight();
  let sigY = Math.max(afterTableY + 20, pageHeight - 40);

  doc.setDrawColor(120, 120, 120);
  doc.setLineWidth(0.3);

  const sigLineWidth = 60;
  // Issued By (left)
  doc.line(marginX, sigY, marginX + sigLineWidth, sigY);
  doc.setFontSize(9);
  doc.setTextColor(90, 90, 90);
  doc.setFont('helvetica', 'normal');
  doc.text('Issued By', marginX, sigY + 5);

  // Received By (right)
  const recX = pageWidth - marginX - sigLineWidth;
  doc.line(recX, sigY, recX + sigLineWidth, sigY);
  doc.text('Received By', recX, sigY + 5);

  // ─── Footer ───────────────────────────────────────
  doc.setFontSize(7.5);
  doc.setTextColor(160, 160, 160);
  doc.text(
    `Generated on ${new Date().toLocaleString('en-GB')} · ${COMPANY.website}`,
    pageWidth / 2,
    pageHeight - 8,
    { align: 'center' }
  );

  // ─── Save / download ──────────────────────────────
  doc.save(`${gp.gp_no || 'GatePass'}.pdf`);
};